// src/pages/OnStreetMasterData.jsx
//
// Off-Street Master Data page ki tarah, is page mein bhi DO views hain:
//   1) Hierarchy View  — City → Street → PND (Device) tree, har device ki
//                         coordinates map par (OnStreetMapEditor) update ki
//                         ja sakti hain, City/Street level par saare devices
//                         ek saath map par bhi dekhe ja sakte hain.
//   2) Tabular View    — bilkul wahi flat/classic table jo pehle is file mein
//                         thi (server-side paginated, search, sort).
//
// On-street ka data model off-street se simpler hai — koi polygon nahi hota,
// sirf 3 levels (City → Street → Device), isliye Hierarchy View bhi 3-level
// tree hai (off-street ki 6-level Country→...→Device tree ke bajaye), aur
// polygon editor ki jagah `OnStreetMapEditor` (sirf point/coords editor) use
// hota hai.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, SlidersHorizontal, ChevronDown, ChevronRight,
  Building2, Navigation, CircleDot, MapPinned, Pencil, Copy, Trash2, Loader2,
} from 'lucide-react';
import { apiCall } from '@/api/apiCall';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/common/DataTable';
import SeeMore from '@/components/common/SeeMore';
import useDebounce from '@/hooks/useDebounce';
import LoadingComponent from '@/components/common/LoadingComponent';
import ReactSelect from '@/components/common/ReactSelect';
import OnStreetMapEditor from '@/components/child/OnStreetMapEditor';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/* ============================================================================
 * SECTION 1 — SHARED HELPERS
 * ==========================================================================*/

const buildKey = (...parts) => parts.map((p) => (p === undefined || p === null || p === '' ? 'x' : p)).join('/');

// row_id kai naam se aa sakta hai — sab variants handle karo
const extractRowId = (r) => {
  if (!r) return '';
  return (
    r.row_id ||
    r.rowId ||
    r.ROWID ||
    r.ROW_ID ||
    r.rowid ||
    r.RowId ||
    r.RowID ||
    r.id_row ||
    ''
  );
};

const getDeviceTypeColor = (deviceType) => {
  if (deviceType?.toLowerCase().includes('bna')) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
  }
  return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
};

const COMPACT_SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 30, height: 30, fontSize: '12px' }),
  valueContainer: (base) => ({ ...base, height: 30, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 30 }),
  indicatorSeparator: (base) => ({ ...base, marginTop: 6, marginBottom: 6 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  option: (base) => ({ ...base, fontSize: '12px', padding: '4px 8px' }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px' }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

function FilterSelect({ label, value, onChange, options, placeholder = 'All' }) {
  const selectOptions = options.map((o) => ({ value: o.id, label: o.name }));
  return (
    <div className="min-w-[170px]">
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">{label}</label>
      <ReactSelect
        options={selectOptions}
        value={selectOptions.find((o) => o.value === value) || null}
        onChange={(s) => onChange(s?.value || '')}
        placeholder={placeholder}
        isClearable
        styles={COMPACT_SELECT_STYLES}
      />
    </div>
  );
}

/* ============================================================================
 * SECTION 2 — SERVICE LAYER
 * ==========================================================================*/

// Classic/tabular view — server-side paginated endpoint.
async function fetchOnStreetPage({ page = 1, limit = 50, sortBy, sortOrder, search, navigate }) {
  let url = `/cp-map/pnd-street/all?page=${page}&limit=${limit}`;
  if (sortBy && sortOrder) url += `&sortBy=${sortBy}&sortDir=${sortOrder}`;
  if (search?.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
  return apiCall('GET', url, null, navigate);
}

const HIERARCHY_PAGE_SIZE = 500;

async function fetchOnStreetHierarchyPage({ page = 1, search, navigate }) {
  return fetchOnStreetPage({
    page,
    limit: HIERARCHY_PAGE_SIZE,
    sortBy: 'city_name',
    sortOrder: 'ASC',
    search,
    navigate,
  });
}

// ── Full dataset meta cache (cities + streets + device types + street anchors) ──
let _allOnStreetMetaPromise = null;

async function fetchAllOnStreetMeta(navigate) {
  if (_allOnStreetMetaPromise) return _allOnStreetMetaPromise;

  _allOnStreetMetaPromise = (async () => {
    const cityMap = new Map();
    const streetsByCity = new Map();
    const deviceTypeSet = new Set();
    // streetKey = `${city_cd}|${street_cd}` → { row_id, latitude, longitude }
    const streetAnchors = new Map();
    let page = 1;
    let totalPages = 1;
    const MAX_PAGES = 200;

    do {
      const res = await fetchOnStreetPage({
        page,
        limit: 500,
        sortBy: 'city_name',
        sortOrder: 'ASC',
        navigate,
      });
      if (!res?.success) break;
      (res.data || []).forEach((r) => {
        if (r.city_cd && !cityMap.has(r.city_cd)) {
          cityMap.set(r.city_cd, r.city_name || r.city_cd);
        }
        if (r.city_cd && r.street_cd) {
          if (!streetsByCity.has(r.city_cd)) streetsByCity.set(r.city_cd, new Map());
          const m = streetsByCity.get(r.city_cd);
          if (!m.has(r.street_cd)) m.set(r.street_cd, r.street_name || r.street_cd);

          const key = `${r.city_cd}|${r.street_cd}`;
          const hasCoords = r.latitude && r.longitude && !isNaN(parseFloat(r.latitude)) && !isNaN(parseFloat(r.longitude));
          const rowId = extractRowId(r);
          const existing = streetAnchors.get(key);

          if (!existing) {
            streetAnchors.set(key, {
              row_id: rowId,
              latitude: r.latitude || '',
              longitude: r.longitude || '',
            });
          } else if (!existing.row_id && rowId) {
            streetAnchors.set(key, {
              row_id: rowId,
              latitude: hasCoords ? r.latitude : existing.latitude,
              longitude: hasCoords ? r.longitude : existing.longitude,
            });
          } else if (hasCoords && !(existing.latitude && existing.longitude)) {
            streetAnchors.set(key, {
              row_id: existing.row_id || rowId,
              latitude: r.latitude,
              longitude: r.longitude,
            });
          }
        }
        if (r.device_type) deviceTypeSet.add(String(r.device_type).trim());
      });
      totalPages = res.totalPages || 1;
      page += 1;
    } while (page <= totalPages && page <= MAX_PAGES);

    const cityOptions = Array.from(cityMap, ([value, label]) => ({ value, label }));
    const streetOptionsFor = (cityCd) => {
      const m = streetsByCity.get(cityCd);
      return m ? Array.from(m, ([value, label]) => ({ value, label })) : [];
    };
    const deviceTypeOptions = Array.from(deviceTypeSet)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }));

    return { cityOptions, streetOptionsFor, deviceTypeOptions, streetAnchors };
  })();

  _allOnStreetMetaPromise = _allOnStreetMetaPromise.catch((err) => {
    _allOnStreetMetaPromise = null;
    throw err;
  });

  return _allOnStreetMetaPromise;
}

/* ────────────────────────────────────────────────────────────────────────────
 * CREATE / MOVE device — POST /cb/on-street/device/update
 * Body: { row_id (anchor), pnd_number, device_type, latitude, longitude }
 * Used for creating a new device AND for saving a moved/dragged pin's
 * new coordinates (see handleDeviceCoordsSave).
 * ──────────────────────────────────────────────────────────────────────────── */
async function createOnStreetDeviceApi(payload) {
  const body = {
    row_id: payload.row_id,
    pnd_number: payload.pnd_number,
    device_type: payload.device_type,
    latitude: String(payload.latitude ?? ''),
    longitude: String(payload.longitude ?? ''),
  };
  const res = await apiCall('POST', '/on-street/device/update', body, null);
  if (res?.success) return res.fields?.data ?? res.data ?? payload;
  throw new Error(res?.message || 'Failed to create device');
}

/* ────────────────────────────────────────────────────────────────────────────
 * UPDATE device — abhi placeholder; agar backend real endpoint de to
 * path/method replace kar dena, baaki UI same rahega.
 * ──────────────────────────────────────────────────────────────────────────── */
async function updateOnStreetDeviceApi(pndNumber, payload) {
  const res = await apiCall('PUT', `/on-street-device/${pndNumber}`, payload, null);
  if (res?.success) return res.fields?.data ?? res.data ?? payload;
  throw new Error(res?.message || 'Failed to update device');
}

/* ────────────────────────────────────────────────────────────────────────────
 * DUPLICATE device — POST /cb/on-street/device/duplicate
 * Body: { row_id, pnd_number, device_type }
 * ──────────────────────────────────────────────────────────────────────────── */
async function duplicateOnStreetDeviceApi(payload) {
  const body = {
    row_id: payload.row_id,
    pnd_number: payload.pnd_number,
    device_type: payload.device_type,
  };
  const res = await apiCall('POST', '/on-street/device/duplicate', body, null);
  if (res?.success) return res.fields?.data ?? res.data ?? payload;
  throw new Error(res?.message || 'Failed to duplicate device');
}

/* ────────────────────────────────────────────────────────────────────────────
 * DELETE device — POST /cb/on-street/device/delete
 * Body: { row_id }
 * ──────────────────────────────────────────────────────────────────────────── */
async function deleteOnStreetDeviceApi(rowId) {
  if (!rowId) throw new Error('row_id is required to delete device');
  const res = await apiCall('POST', '/on-street/device/delete', { row_id: rowId }, null);
  if (res?.success) return true;
  throw new Error(res?.message || 'Failed to delete device');
}

const DEVICE_STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

/* ============================================================================
 * SECTION 3 — BUILD 3-LEVEL HIERARCHY (CITY → STREET → DEVICE)
 * ==========================================================================*/

function normalizeOnStreetHierarchy(flatRows = []) {
  const rows = [];
  const cityMap = new Map();

  flatRows.forEach((r) => {
    const cityKey = r.city_cd || r.city_name;
    if (!cityMap.has(cityKey)) cityMap.set(cityKey, { city_cd: r.city_cd, city_name: r.city_name, streets: new Map() });
    const cityGroup = cityMap.get(cityKey);
    const streetKey = r.street_cd || r.street_name;
    if (!cityGroup.streets.has(streetKey)) {
      cityGroup.streets.set(streetKey, { street_cd: r.street_cd, street_name: r.street_name, devices: [] });
    }
    cityGroup.streets.get(streetKey).devices.push(r);
  });

  cityMap.forEach((cityGroup, cityKey) => {
    const cityId = buildKey(cityKey);
    let cityDeviceCount = 0;

    cityGroup.streets.forEach((streetGroup, streetKey) => {
      const streetId = buildKey(cityId, streetKey);

      streetGroup.devices.forEach((device) => {
        const hasCoords = !!(device.latitude && device.longitude && !isNaN(parseFloat(device.latitude)) && !isNaN(parseFloat(device.longitude)));
        rows.push({
          level: 'DEVICE',
          id: buildKey(streetId, device.pnd_number),
          parentId: streetId,
          name: device.pnd_number,
          code: device.pnd_number,
          deviceType: device.device_type || null,
          status: device.device_status || null,
          coordStatus: hasCoords ? 'Coordinates Set' : 'Coordinates Missing',
          lat: hasCoords ? parseFloat(device.latitude) : null,
          lng: hasCoords ? parseFloat(device.longitude) : null,
          childCount: 0,
          raw: device,
        });
        cityDeviceCount += 1;
      });

      rows.push({
        level: 'STREET',
        id: streetId,
        parentId: cityId,
        name: streetGroup.street_name || streetGroup.street_cd || 'Street',
        code: streetGroup.street_cd ?? '',
        childCount: streetGroup.devices.length,
        raw: streetGroup,
      });
    });

    rows.push({
      level: 'CITY',
      id: cityId,
      parentId: null,
      name: cityGroup.city_name || cityGroup.city_cd || 'City',
      code: cityGroup.city_cd ?? '',
      childCount: cityGroup.streets.size,
      deviceCount: cityDeviceCount,
      raw: cityGroup,
    });
  });

  return rows;
}

const indexByParent = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.parentId ?? '__root__';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return map;
};

const buildBreadcrumb = (row, rowsById) => {
  const trail = [];
  let cur = row;
  while (cur) {
    trail.unshift({ level: cur.level, id: cur.id, name: cur.name });
    cur = cur.parentId ? rowsById.get(cur.parentId) : null;
  }
  return trail;
};

/* ============================================================================
 * SECTION 4 — HIERARCHY TREE TABLE
 * ==========================================================================*/

const LEVEL_META = {
  CITY: { icon: Building2 },
  STREET: { icon: Navigation, addLabel: 'Add Device' },
  DEVICE: { icon: CircleDot },
};
const CHILD_COUNT_LABEL = {
  CITY: (r) => `${r.childCount} streets · ${r.deviceCount} devices`,
  STREET: (r) => `${r.childCount} devices`,
  DEVICE: () => null,
};
const HIERARCHY_LEVEL_ORDER = ['CITY', 'STREET', 'DEVICE'];
const EXPAND_LEVEL_OPTIONS = [
  { level: 'STREET', label: 'Street' },
  { level: 'DEVICE', label: 'All' },
];
const COLLAPSE_LEVEL_OPTIONS = [
  { level: 'CITY', label: 'City' },
  { level: 'STREET', label: 'Street' },
];

function TreeRow({ row, depth, rowsIndex, expanded, onToggle, onAction, highlightId }) {
  const meta = LEVEL_META[row.level];
  const Icon = meta.icon;
  const children = rowsIndex.get(row.id) || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(row.id);
  const isDevice = row.level === 'DEVICE';
  const isHighlighted = row.id === highlightId;
  const cellStateBg = isHighlighted ? 'bg-primary/10' : 'bg-background group-hover:bg-secondary/40';

  return (
    <>
      <tr id={`osrow-${row.id}`} className="group border-b border-border/60 transition-colors">
        <td className={`px-2 py-2 sticky left-0 transition-colors ${cellStateBg}`}>
          <div className="flex items-center gap-1.5" style={{ paddingLeft: depth * 18 }}>
            {hasChildren ? (
              <button onClick={() => onToggle(row.id)} className="p-0.5 rounded hover:bg-secondary text-muted-foreground">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-semibold text-foreground truncate max-w-[220px]" title={row.name}>{row.name}</span>
          </div>
        </td>
        <td className={`px-2 py-2 text-[11px] text-muted-foreground transition-colors ${cellStateBg}`}>{row.code || '—'}</td>
        <td className={`px-2 py-2 transition-colors ${cellStateBg}`}>
          {isDevice ? (
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getDeviceTypeColor(row.deviceType)}`}>
              {row.deviceType || '—'}
            </span>
          ) : '—'}
        </td>
        <td className={`px-2 py-2 text-[11px] text-muted-foreground transition-colors ${cellStateBg}`}>
          {CHILD_COUNT_LABEL[row.level](row)}
        </td>
        <td className={`px-2 py-2 text-[11px] font-mono text-muted-foreground transition-colors ${cellStateBg}`}>
          {isDevice ? (row.lat != null ? `${row.lat.toFixed(5)}, ${row.lng.toFixed(5)}` : '—') : '—'}
        </td>
        <td className={`px-2 py-2 transition-colors ${cellStateBg}`}>
          {isDevice ? (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.coordStatus === 'Coordinates Set' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-600'}`}>
              {row.coordStatus}
            </span>
          ) : '—'}
        </td>
        <td className={`px-2 py-2 transition-colors ${cellStateBg}`}>
          {isDevice ? (
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              row.status === 'ACTIVE'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
            }`}>
              {row.status || '—'}
            </span>
          ) : <span className="text-emerald-500 font-semibold text-[11px]">Active</span>}
        </td>
        <td className={`px-2 py-2 sticky right-0 transition-colors ${cellStateBg}`}>
          <div className="flex items-center gap-1 justify-end">
            {isDevice ? (
              <>
                <button title="Update Coordinates" onClick={() => onAction('coords', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><MapPinned className="w-3.5 h-3.5" /></button>
                {/* <button title="Delete Device" onClick={() => onAction('delete', row)} className="p-1.5 rounded-md hover:bg-red-500/10 text-muted-foreground hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button> */}
              </>
            ) : (
              <>
                {row.level === 'STREET' && (
                  <button title={meta.addLabel} onClick={() => onAction('addChild', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Plus className="w-3.5 h-3.5" /></button>
                )}
                <button title="View on Map" onClick={() => onAction('view', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><MapPinned className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </td>
      </tr>
      {isOpen && children.map((child) => (
        <TreeRow key={child.id} row={child} depth={depth + 1} rowsIndex={rowsIndex} expanded={expanded} onToggle={onToggle} onAction={onAction} highlightId={highlightId} />
      ))}
    </>
  );
}

function HierarchyTreeTable({ rootRows, rowsIndex, onAction, highlightId, expandedIds, onScrollNearBottom, loadingMore, hasMore }) {
  const [expanded, setExpanded] = useState(() => new Set(expandedIds || []));
  useEffect(() => { if (expandedIds) setExpanded(new Set(expandedIds)); }, [expandedIds]);
  const toggle = (id) => setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onScrollNearBottom || loadingMoreRef.current || !hasMore) return;
    const threshold = 200;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      onScrollNearBottom();
    }
  }, [onScrollNearBottom, hasMore]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="overflow-auto border border-border rounded-xl max-h-[calc(100vh-200px)]"
    >
      <table className="w-full text-left border-collapse min-w-[900px]">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr className="border-b border-border">
            {['Name', 'Code', 'Device Type', 'Children / Devices', 'Coordinates', 'Coord Status', 'Status', 'Actions'].map((h) => (
              <th key={h} className="px-2 py-2 text-[9px] uppercase tracking-widest font-bold text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rootRows.map((row) => (
            <TreeRow key={row.id} row={row} depth={0} rowsIndex={rowsIndex} expanded={expanded} onToggle={toggle} onAction={onAction} highlightId={highlightId} />
          ))}
        </tbody>
      </table>
      {(loadingMore || hasMore) && (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground border-t border-border">
          {loadingMore ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading more...</>
          ) : (
            <span className="opacity-60">Scroll for more</span>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * SECTION 4.5 — CRUD DIALOGS
 * ==========================================================================*/

function CreatableSelectField({ label, required, options, value, onChange, onCreate, placeholder, isDisabled }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const skipBlurRef = useRef(false);

  const commit = () => {
    const v = draft.trim();
    if (v) onCreate(v);
    setAdding(false);
    setDraft('');
  };

  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-muted-foreground mb-1">{label}{required ? ' *' : ''}</span>
      {adding ? (
        <input
          autoFocus
          className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={`Type new ${label.toLowerCase()}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (skipBlurRef.current) { skipBlurRef.current = false; return; } commit(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); skipBlurRef.current = true; commit(); }
            else if (e.key === 'Escape') { skipBlurRef.current = true; setAdding(false); setDraft(''); }
          }}
        />
      ) : (
        <ReactSelect
          placeholder={placeholder || `Select ${label.toLowerCase()}`}
          controlHeight={32}
          isClearable
          isDisabled={isDisabled}
          options={[{ value: '__ADD_NEW__', label: <span className="text-[10px] font-semibold">+ Add New {label}</span> }, ...options]}
          value={value ? (options.find((o) => o.value === value) || { value, label: value }) : null}
          onChange={(opt) => {
            if (opt?.value === '__ADD_NEW__') { setAdding(true); setDraft(''); return; }
            onChange(opt?.value || '');
          }}
        />
      )}
    </label>
  );
}

// Nearby device on same street — full dataset anchor ya loaded rows se.
function findNearbyDeviceOnStreet(flatRows, streetCd, cityCd, streetAnchors) {
  if (!streetCd) return null;

  if (streetAnchors && cityCd) {
    const key = `${cityCd}|${streetCd}`;
    const anchor = streetAnchors.get?.(key) || streetAnchors[key];
    if (anchor && (anchor.row_id || anchor.latitude)) {
      return {
        row_id: anchor.row_id || null,
        ROWID: anchor.row_id || null,
        latitude: anchor.latitude || '',
        longitude: anchor.longitude || '',
      };
    }
  }

  if (!Array.isArray(flatRows) || !flatRows.length) return null;
  const onStreet = flatRows.filter((r) => {
    if (String(r.street_cd) !== String(streetCd)) return false;
    if (cityCd && r.city_cd && String(r.city_cd) !== String(cityCd)) return false;
    return true;
  });
  if (!onStreet.length) return null;
  const withCoords = onStreet.find((r) => r.latitude && r.longitude && !isNaN(parseFloat(r.latitude)) && !isNaN(parseFloat(r.longitude)));
  return withCoords || onStreet[0];
}

function collectDeviceTypeOptions(flatRows = []) {
  const set = new Set();
  flatRows.forEach((r) => {
    if (r.device_type) set.add(String(r.device_type).trim());
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b)).map((t) => ({ value: t, label: t }));
}

/* ── Add/Edit Device Form ────────────────────────────────────────────────── */
function DeviceFormDialog({
  presetParent, editingRow, cityOptions, streetOptionsFor,
  deviceTypeOptions: initialDeviceTypeOptions = [],
  flatRows = [],
  streetAnchors = null,
  onCancel, onSubmit,
}) {
  const raw = editingRow?.raw || {};
  const initialStreetCd = presetParent?.street_cd ?? raw.street_cd ?? '';
  const initialCityCd = presetParent?.city_cd ?? raw.city_cd ?? '';
  const nearby = !editingRow ? findNearbyDeviceOnStreet(flatRows, initialStreetCd, initialCityCd, streetAnchors) : null;

  const [form, setForm] = useState(() => ({
    city_cd: initialCityCd,
    city_name: presetParent?.city_name ?? raw.city_name ?? '',
    street_cd: initialStreetCd,
    street_name: presetParent?.street_name ?? raw.street_name ?? '',
    pnd_number: raw.pnd_number ?? '',
    device_type: raw.device_type ?? '',
    device_status: 'ACTIVE',
    latitude: raw.latitude ?? nearby?.latitude ?? '',
    longitude: raw.longitude ?? nearby?.longitude ?? '',
    row_id: raw.row_id ?? raw.ROWID ?? raw.rowid ?? nearby?.row_id ?? nearby?.ROWID ?? '',
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState(initialDeviceTypeOptions);

  useEffect(() => { setDeviceTypeOptions(initialDeviceTypeOptions); }, [initialDeviceTypeOptions]);

  const isCityLocked = !!presetParent?.city_cd;
  const isStreetLocked = !!presetParent?.street_cd;
  const streetOptions = useMemo(() => streetOptionsFor(form.city_cd), [streetOptionsFor, form.city_cd]);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const applyStreetDefaults = (streetCd, cityCd) => {
    const near = findNearbyDeviceOnStreet(flatRows, streetCd, cityCd, streetAnchors);
    if (!near) { setForm((p) => ({ ...p, row_id: '' })); return; }
    setForm((p) => ({
      ...p,
      latitude: near.latitude || '',
      longitude: near.longitude || '',
      row_id: near.row_id || near.ROWID || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.city_cd || !form.street_cd) { setError('City aur Street dono select/create karna zaroori hai.'); return; }
    if (!form.pnd_number?.trim()) { setError('PND Number is required.'); return; }
    if (!form.device_type?.trim()) { setError('Device Type is required.'); return; }
    let rowId = form.row_id;
    if (!editingRow && !rowId) {
      const near = findNearbyDeviceOnStreet(flatRows, form.street_cd, form.city_cd, streetAnchors);
      rowId = near?.row_id || near?.ROWID || '';
    }
    if (!editingRow && !rowId) {
      setError('Is street par koi existing device nahi mila (row_id required). List API se row_id nahi aa raha — backend check karein.');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      await onSubmit({
        ...form,
        pnd_number: form.pnd_number.trim(),
        device_type: form.device_type.trim(),
        device_status: 'ACTIVE',
        row_id: rowId,
      });
    } catch (err) {
      setError(err?.message || 'Failed to save device');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-lg p-4">
        <DialogHeader className="pb-1">
          <DialogTitle className="text-base">{editingRow ? 'Edit Device' : 'Add Device'}</DialogTitle>
          <DialogDescription className="text-sm">
            {editingRow ? 'Update this PND device\'s details below.' : 'Pick the City and Street this device belongs to, then fill in its details. Status will be ACTIVE.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2">
              <CreatableSelectField
                label="City" required
                options={cityOptions}
                value={form.city_cd}
                isDisabled={isCityLocked}
                onChange={(v) => {
                  const opt = cityOptions.find((o) => o.value === v);
                  setField('city_cd', v);
                  setField('city_name', opt?.label || v);
                  setField('street_cd', ''); setField('street_name', '');
                  setField('row_id', '');
                }}
                onCreate={(name) => { setField('city_cd', name); setField('city_name', name); setField('street_cd', ''); setField('street_name', ''); setField('row_id', ''); }}
              />
              <CreatableSelectField
                label="Street" required
                options={streetOptions}
                value={form.street_cd}
                isDisabled={isStreetLocked || !form.city_cd}
                onChange={(v) => {
                  const opt = streetOptions.find((o) => o.value === v);
                  setField('street_cd', v);
                  setField('street_name', opt?.label || v);
                  applyStreetDefaults(v, form.city_cd);
                }}
                onCreate={(name) => { setField('street_cd', name); setField('street_name', name); }}
              />
            </div>

            <div className="border-t pt-3 space-y-3">
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">PND Number *</span>
                <input
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                  value={form.pnd_number}
                  disabled={!!editingRow}
                  onChange={(e) => setField('pnd_number', e.target.value)}
                  placeholder="Enter PND number"
                />
              </label>

              <CreatableSelectField
                label="Device Type"
                required
                options={deviceTypeOptions}
                value={form.device_type}
                onChange={(v) => setField('device_type', v)}
                onCreate={(name) => {
                  const trimmed = name.trim();
                  if (!trimmed) return;
                  setDeviceTypeOptions((prev) => {
                    if (prev.some((o) => o.value === trimmed)) return prev;
                    return [...prev, { value: trimmed, label: trimmed }].sort((a, b) => a.label.localeCompare(b.label));
                  });
                  setField('device_type', trimmed);
                }}
                placeholder="Select device type"
              />

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={form.latitude}
                    onChange={(e) => setField('latitude', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={form.longitude}
                    onChange={(e) => setField('longitude', e.target.value)}
                  />
                </label>
              </div>
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting} className="w-full sm:w-auto h-8 text-sm">Cancel</Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto h-8 text-sm">
              {submitting ? (<><Loader2 className="mr-2 h-3 w-3 animate-spin" />Saving...</>) : editingRow ? 'Save Changes' : (<><Plus className="mr-2 h-3.5 w-3.5" />Create Device</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Duplicate Device Dialog ──────────────────────────────────────────────
 * Backend: POST /cb/on-street/device/duplicate
 * Body:    { row_id, pnd_number, device_type }
 * ──────────────────────────────────────────────────────────────────────── */
function DuplicateDeviceDialog({ sourceRow, onCancel, onSubmit }) {
  const raw = sourceRow.raw || {};
  const [form, setForm] = useState({
    pnd_number: '',
    device_type: raw.device_type ?? '',
    device_status: raw.device_status ?? 'ACTIVE',
    device_zone: raw.device_zone ?? '',
    device_zone_name: raw.device_zone_name ?? '',
    latitude: raw.latitude ?? '',
    longitude: raw.longitude ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (!form.pnd_number?.trim()) { setError('New PND Number is required.'); return; }
    if (!form.device_type?.trim()) { setError('Device Type is required.'); return; }
    setSubmitting(true); setError(null);
    try {
      await onSubmit({
        ...form,
        pnd_number: form.pnd_number.trim(),
        device_type: form.device_type.trim(),
        row_id: extractRowId(raw),
        source_pnd_number: raw.pnd_number,
        city_cd: raw.city_cd,
        city_name: raw.city_name,
        street_cd: raw.street_cd,
        street_name: raw.street_name,
      });
    } catch (err) {
      setError(err?.message || 'Failed to duplicate device');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-md p-4">
        <DialogHeader className="pb-1">
          <DialogTitle className="text-base">Duplicate Device</DialogTitle>
          <DialogDescription className="text-sm">
            Duplicating <strong>{raw.pnd_number}</strong> on {raw.street_name || raw.street_cd}, {raw.city_name || raw.city_cd}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block">
            <span className="block text-[10px] font-semibold text-muted-foreground mb-1">New PND Number *</span>
            <input
              autoFocus
              className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.pnd_number}
              onChange={(e) => setField('pnd_number', e.target.value)}
              placeholder="Enter new PND number"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type</span>
              <input
                className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                value={form.device_type}
                onChange={(e) => setField('device_type', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Status</span>
              <select
                className="w-full px-2.5 py-[7px] rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                value={form.device_status}
                onChange={(e) => setField('device_status', e.target.value)}
              >
                {DEVICE_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
              <input
                className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                value={form.latitude}
                onChange={(e) => setField('latitude', e.target.value)}
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
              <input
                className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                value={form.longitude}
                onChange={(e) => setField('longitude', e.target.value)}
              />
            </label>
          </div>
        </div>

        {error && (
          <div className="text-[11px] text-red-500 mt-2 p-2 bg-red-500/10 rounded-md border border-red-500/20">
            {error}
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting} className="w-full sm:w-auto h-8 text-sm">Cancel</Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto h-8 text-sm">
            {submitting ? (<><Loader2 className="mr-2 h-3 w-3 animate-spin" />Duplicating...</>) : (<><Copy className="mr-2 h-3.5 w-3.5" />Duplicate Device</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Delete Device Dialog ─────────────────────────────────────────────────── */
function DeleteDeviceDialog({ row, onCancel, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const raw = row.raw || {};

  const handleConfirm = async () => {
    setDeleting(true); setError(null);
    try { await onConfirm(); }
    catch (err) { setError(err?.message || 'Failed to delete device'); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !deleting) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-md p-4">
        <DialogHeader className="pb-1">
          <DialogTitle className="text-destructive text-base">Delete Device</DialogTitle>
          <DialogDescription className="text-sm">
            Are you sure you want to delete this device? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 px-3 border rounded-lg bg-muted/30">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">PND Number</span>
              <span className="text-sm font-medium truncate max-w-[220px]">{raw.pnd_number || row.name || '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Street</span>
              <span className="text-sm truncate max-w-[220px]">{raw.street_name || raw.street_cd || '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1 border-t">
              <span className="text-xs text-muted-foreground">Device Type</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${getDeviceTypeColor(raw.device_type)}`}>
                {raw.device_type || '—'}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="text-[11px] text-red-500 mt-2 p-2 bg-red-500/10 rounded-md border border-red-500/20">
            {error}
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={deleting} className="w-full sm:w-auto h-8 text-sm">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={deleting}
            className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90 h-8 text-sm"
          >
            {deleting ? (
              <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Deleting...</>
            ) : (
              <><Trash2 className="mr-2 h-3.5 w-3.5" />Delete Device</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildCityStreetOptions(flatRows = []) {
  const cityMap = new Map();
  const streetsByCity = new Map();
  flatRows.forEach((r) => {
    if (r.city_cd && !cityMap.has(r.city_cd)) cityMap.set(r.city_cd, r.city_name || r.city_cd);
    if (r.city_cd && r.street_cd) {
      if (!streetsByCity.has(r.city_cd)) streetsByCity.set(r.city_cd, new Map());
      const m = streetsByCity.get(r.city_cd);
      if (!m.has(r.street_cd)) m.set(r.street_cd, r.street_name || r.street_cd);
    }
  });
  const cityOptions = Array.from(cityMap, ([value, label]) => ({ value, label }));
  const streetOptionsFor = (cityCd) => {
    const m = streetsByCity.get(cityCd);
    return m ? Array.from(m, ([value, label]) => ({ value, label })) : [];
  };
  return { cityOptions, streetOptionsFor };
}

/* ============================================================================
 * SECTION 5 — HIERARCHY VIEW
 * ==========================================================================*/

const HierarchyView = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [flatRows, setFlatRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreLock = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const isSearchMode = !!debouncedSearch.trim();
  // Server-side search results (hierarchy me sirf loaded pages hote hain, isliye
  // search ke liye backend se poora match lana zaroori hai — tabular view ki tarah)
  const [searchRows, setSearchRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchNonce, setSearchNonce] = useState(0);
  const searchReqId = useRef(0);
  const [filters, setFilters] = useState({ city: '', street: '', deviceType: '', status: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [mapState, setMapState] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandLevel, setExpandLevel] = useState('CITY');

  const [fullMeta, setFullMeta] = useState({
    cityOptions: [],
    streetOptionsFor: () => [],
    deviceTypeOptions: [],
    streetAnchors: new Map(),
  });
  useEffect(() => {
    let cancelled = false;
    fetchAllOnStreetMeta(navigate)
      .then((meta) => { if (!cancelled) setFullMeta(meta); })
      .catch((err) => console.error('Failed to load full on-street meta:', err));
    return () => { cancelled = true; };
  }, [navigate]);

  const [formState, setFormState] = useState(null);
  const [duplicateState, setDuplicateState] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setSearchNonce((n) => n + 1); // search mode me bhi data refresh ho
    setLoading(true); setError(null); setPage(1); setHasMore(true);
    loadMoreLock.current = false;
    try {
      const res = await fetchOnStreetHierarchyPage({ page: 1, navigate });
      if (!res?.success) throw new Error(res?.message || 'Failed to load on-street data');
      setFlatRows(res.data || []);
      const totalPages = res.totalPages || 1;
      setHasMore(totalPages > 1);
      setPage(1);
    } catch (err) {
      setError(err?.message || 'Failed to load on-street hierarchy');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    if (isSearchMode) return; // search results already poore fetch ho chuke hote hain
    if (loadMoreLock.current || !hasMore || loading || loadingMore) return;
    loadMoreLock.current = true;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await fetchOnStreetHierarchyPage({ page: nextPage, navigate });
      if (res?.success) {
        const batch = res.data || [];
        setFlatRows((prev) => prev.concat(batch));
        const totalPages = res.totalPages || 1;
        setPage(nextPage);
        setHasMore(nextPage < totalPages);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load more on-street data:', err);
    } finally {
      setLoadingMore(false);
      loadMoreLock.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page, isSearchMode]);

  useEffect(() => { load(); }, [load]);

  // Server-side search: matching rows ke saare pages fetch karke tree banta hai
  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) { setSearchRows([]); setSearching(false); return undefined; }
    const reqId = ++searchReqId.current;
    setSearching(true);
    (async () => {
      try {
        let p = 1; let totalPages = 1; const MAX_PAGES = 50; let acc = [];
        do {
          const res = await fetchOnStreetHierarchyPage({ page: p, search: q, navigate });
          if (reqId !== searchReqId.current) return; // purani request ignore
          if (!res?.success) break;
          acc = acc.concat(res.data || []);
          totalPages = res.totalPages || 1;
          p += 1;
        } while (p <= totalPages && p <= MAX_PAGES);
        if (reqId === searchReqId.current) setSearchRows(acc);
      } catch (err) {
        console.error('Hierarchy search failed:', err);
        if (reqId === searchReqId.current) setSearchRows([]);
      } finally {
        if (reqId === searchReqId.current) setSearching(false);
      }
    })();
    return () => { /* reqId guard stale responses handle karta hai */ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, searchNonce]);

  const allRows = useMemo(
    () => normalizeOnStreetHierarchy(isSearchMode ? searchRows : flatRows),
    [isSearchMode, searchRows, flatRows]
  );
  const rowsIndex = useMemo(() => indexByParent(allRows), [allRows]);
  const rowsById = useMemo(() => new Map(allRows.map((r) => [r.id, r])), [allRows]);
  const rootRows = useMemo(() => rowsIndex.get('__root__') || [], [rowsIndex]);
  const { cityOptions, streetOptionsFor } = useMemo(() => buildCityStreetOptions(flatRows), [flatRows]);
  const deviceTypeOptions = useMemo(() => collectDeviceTypeOptions(flatRows), [flatRows]);

  useEffect(() => {
    if (!mapState) return;
    const fresh = rowsById.get(mapState.row.id);
    if (fresh && fresh !== mapState.row) {
      setMapState((prev) => (prev ? { ...prev, row: fresh } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsById]);

  const expandToLevel = useCallback((targetLevel) => {
    const targetIdx = HIERARCHY_LEVEL_ORDER.indexOf(targetLevel);
    const ids = allRows
      .filter((r) => HIERARCHY_LEVEL_ORDER.indexOf(r.level) < targetIdx && rowsIndex.has(r.id))
      .map((r) => r.id);
    setExpandedIds(new Set(ids));
    setExpandLevel(targetLevel);
  }, [allRows, rowsIndex]);

  useEffect(() => {
    if (!rootRows.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      rootRows.forEach((r) => next.add(r.id));
      return next;
    });
  }, [rootRows]);

  const availableCities = useMemo(() => allRows.filter((r) => r.level === 'CITY'), [allRows]);
  const availableStreets = useMemo(
    () => allRows.filter((r) => r.level === 'STREET' && (!filters.city || r.parentId === filters.city)),
    [allRows, filters.city]
  );
  const availableDeviceTypes = useMemo(() => {
    const scopeId = filters.street || filters.city || null;
    const set = new Set();
    allRows.forEach((r) => {
      if (r.level !== 'DEVICE' || !r.deviceType) return;
      if (!scopeId || r.id === scopeId || r.id.startsWith(`${scopeId}/`)) set.add(r.deviceType);
    });
    return Array.from(set).sort();
  }, [allRows, filters.city, filters.street]);
  const STATUS_OPTIONS = [{ id: 'ACTIVE', name: 'ACTIVE' }, { id: 'INACTIVE', name: 'INACTIVE' }];

  const searchMatchRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return null;
    return allRows.filter((r) => String(r.name || '').toLowerCase().includes(q) || String(r.code || '').toLowerCase().includes(q) || String(r.deviceType || '').toLowerCase().includes(q));
  }, [allRows, searchTerm]);

  useEffect(() => {
    if (!searchMatchRows || !searchMatchRows.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      searchMatchRows.forEach((match) => buildBreadcrumb(match, rowsById).forEach((c) => next.add(c.id)));
      return next;
    });
    setHighlightId(searchMatchRows[0]?.id || null);
    const t = setTimeout(() => {
      const el = document.getElementById(`osrow-${searchMatchRows[0]?.id}`);
      if (!el) return;
      const container = el.closest('.overflow-auto');
      if (!container) { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); return; }
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const delta = (elRect.top + elRect.bottom) / 2 - (containerRect.top + containerRect.bottom) / 2;
      container.scrollBy({ top: delta, behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(t);
  }, [searchMatchRows, rowsById]);

  const idIsOnLine = (filterId, id) => id === filterId || id.startsWith(`${filterId}/`) || filterId.startsWith(`${id}/`);

  const buildLeafFilterIds = useCallback((predicate) => {
    const matched = allRows.filter(predicate);
    if (!matched.length) return new Set();
    const keep = new Set();
    matched.forEach((row) => {
      keep.add(row.id);
      let parentId = row.parentId;
      while (parentId) { keep.add(parentId); parentId = rowsById.get(parentId)?.parentId; }
    });
    allRows.forEach((row) => { if (matched.some((m) => row.id.startsWith(`${m.id}/`))) keep.add(row.id); });
    return keep;
  }, [allRows, rowsById]);

  const deviceTypeIds = useMemo(
    () => (filters.deviceType ? buildLeafFilterIds((r) => r.level === 'DEVICE' && r.deviceType === filters.deviceType) : null),
    [filters.deviceType, buildLeafFilterIds]
  );
  const statusIds = useMemo(
    () => (filters.status ? buildLeafFilterIds((r) => r.level === 'DEVICE' && r.status === filters.status) : null),
    [filters.status, buildLeafFilterIds]
  );

  const filteredRowsIndex = useMemo(() => {
    const pathFilterIds = [filters.city, filters.street].filter(Boolean);
    const hasAnyFilter = pathFilterIds.length || deviceTypeIds || statusIds;
    if (!hasAnyFilter) return rowsIndex;
    const kept = allRows.filter((row) => {
      if (!pathFilterIds.every((fid) => idIsOnLine(fid, row.id))) return false;
      if (deviceTypeIds && !deviceTypeIds.has(row.id)) return false;
      if (statusIds && !statusIds.has(row.id)) return false;
      return true;
    });
    return indexByParent(kept);
  }, [allRows, rowsIndex, filters.city, filters.street, deviceTypeIds, statusIds]);

  const visibleRootRows = useMemo(() => filteredRowsIndex.get('__root__') || [], [filteredRowsIndex]);

  const handleAction = useCallback((action, row) => {
    switch (action) {
      case 'coords': setMapState({ row, mode: 'coords' }); break;
      case 'view': setMapState({ row, mode: 'view' }); break;
      case 'addChild': {
        const streetGroup = row.raw || {};
        const cityRow = rowsById.get(row.parentId);
        setFormState({
          presetParent: {
            city_cd: cityRow?.code, city_name: cityRow?.name,
            street_cd: streetGroup.street_cd, street_name: streetGroup.street_name,
          },
        });
        break;
      }
      case 'edit': setFormState({ editingRow: row }); break;
      case 'duplicate': setDuplicateState({ row }); break;
      // case 'delete': setDeleteConfirm({ row }); break;
      default: break;
    }
  }, [rowsById]);

  const handleDeviceCoordsSave = async (row, coords) => {
    const raw = row.raw || {};
    await createOnStreetDeviceApi({
      row_id: extractRowId(raw),
      pnd_number: row.code,
      device_type: raw.device_type,
      latitude: coords.lat,
      longitude: coords.lng,
    });
    setMapState(null);
    await load();
  };

  const handleDeviceFormSubmit = async (payload) => {
    if (formState?.editingRow) {
      await updateOnStreetDeviceApi(formState.editingRow.raw?.pnd_number, payload);
    } else {
      await createOnStreetDeviceApi(payload);
    }
    setFormState(null);
    await load();
  };

  // ── Resolve row_id: source row → streetAnchors → loaded rows ──
  const resolveRowIdForStreet = useCallback((cityCd, streetCd, explicitRowId, loadedRows) => {
    if (explicitRowId) return explicitRowId;

    if (cityCd && streetCd && fullMeta.streetAnchors) {
      const key = `${cityCd}|${streetCd}`;
      const anchor = fullMeta.streetAnchors.get?.(key) || fullMeta.streetAnchors[key];
      if (anchor?.row_id) return anchor.row_id;
    }

    if (streetCd && Array.isArray(loadedRows)) {
      const match = loadedRows.find((r) => {
        if (String(r.street_cd) !== String(streetCd)) return false;
        if (cityCd && String(r.city_cd) !== String(cityCd)) return false;
        return !!extractRowId(r);
      });
      if (match) return extractRowId(match);
    }

    return '';
  }, [fullMeta.streetAnchors]);

  const handleDuplicateSubmit = async (payload) => {
    const rowId = resolveRowIdForStreet(payload.city_cd, payload.street_cd, payload.row_id, flatRows);
    if (!rowId) {
      throw new Error('Is street par koi existing device ka row_id nahi mila. Backend team se kehna hoga ki /cp-map/pnd-street/all mein row_id field add karein.');
    }
    await duplicateOnStreetDeviceApi({
      row_id: rowId,
      pnd_number: payload.pnd_number,
      device_type: payload.device_type,
    });
    setDuplicateState(null);
    await load();
  };

  // const handleDeleteConfirm = async () => {
  //   const raw = deleteConfirm.row.raw || {};
  //   const rowId = resolveRowIdForStreet(raw.city_cd, raw.street_cd, extractRowId(raw), flatRows);
  //   if (!rowId) {
  //     throw new Error('Delete karne ke liye row_id chahiye. Backend team se kehna hoga ki /cp-map/pnd-street/all mein row_id field add karein.');
  //   }
  //   await deleteOnStreetDeviceApi(rowId);
  //   setDeleteConfirm(null);
  //   await load();
  // };

  const handleJumpTo = (childRow, forceMode) => {
    setMapState({ row: childRow, mode: forceMode || (childRow.level === 'DEVICE' ? 'coords' : 'view') });
  };
  const handleBreadcrumbClick = (crumb) => {
    const row = rowsById.get(crumb.id);
    if (!row) return;
    setMapState({ row, mode: row.level === 'DEVICE' ? 'coords' : 'view' });
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-8 text-sm text-red-500 flex items-center gap-3">{error}<button onClick={load} className="underline">Retry</button></div>;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">On-Street Hierarchy & Coordinates Management</h1>
          <p className="text-xs text-muted-foreground">City → Street → PND Device</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 px-3 gap-1.5 flex items-center rounded-lg border border-border text-xs font-semibold hover:bg-secondary">
                <ChevronDown className="h-3.5 w-3.5" />
                Expand: {EXPAND_LEVEL_OPTIONS.find((o) => o.level === expandLevel)?.label || 'City'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuLabel>Expand up to</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {EXPAND_LEVEL_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.level} onClick={() => expandToLevel(opt.level)}>{opt.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 px-3 gap-1.5 flex items-center rounded-lg border border-border text-xs font-semibold hover:bg-secondary">
                <ChevronRight className="h-3.5 w-3.5" /> Collapse
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuLabel>Collapse to</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLLAPSE_LEVEL_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.level} onClick={() => expandToLevel(opt.level)}>{opt.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 flex-wrap">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search hierarchy..."
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground" />
        </div>
        <button onClick={() => setShowFilters((v) => !v)} className={`p-2 rounded-lg border text-xs font-semibold flex items-center gap-1.5 flex-shrink-0 ${showFilters ? 'bg-primary/10 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="flex items-center gap-2 flex-wrap p-3 rounded-xl border border-border bg-card">
          <FilterSelect label="City" value={filters.city} onChange={(v) => setFilters((f) => ({ ...f, city: v, street: '' }))} options={availableCities} />
          <FilterSelect label="Street" value={filters.street} onChange={(v) => setFilters((f) => ({ ...f, street: v }))} options={availableStreets} />
          <div className="min-w-[170px]">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Device Type</label>
            <ReactSelect
              options={availableDeviceTypes.map((t) => ({ value: t, label: t }))}
              value={filters.deviceType ? { value: filters.deviceType, label: filters.deviceType } : null}
              onChange={(s) => setFilters((f) => ({ ...f, deviceType: s?.value || '' }))}
              placeholder="All" isClearable styles={COMPACT_SELECT_STYLES}
            />
          </div>
          <div className="min-w-[170px]">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Status</label>
            <ReactSelect
              options={STATUS_OPTIONS.map((s) => ({ value: s.id, label: s.name }))}
              value={filters.status ? { value: filters.status, label: filters.status } : null}
              onChange={(s) => setFilters((f) => ({ ...f, status: s?.value || '' }))}
              placeholder="All" isClearable styles={COMPACT_SELECT_STYLES}
            />
          </div>
        </div>
      )}

      <HierarchyTreeTable
        rootRows={visibleRootRows}
        rowsIndex={filteredRowsIndex}
        onAction={handleAction}
        highlightId={highlightId}
        expandedIds={expandedIds}
        onScrollNearBottom={loadMore}
        loadingMore={loadingMore || searching}
        hasMore={hasMore && !isSearchMode}
      />

     {mapState && (
  <OnStreetMapEditor
    row={mapState.row}
    rowsIndex={rowsIndex}
    mode={mapState.mode}
    onDeviceSave={handleDeviceCoordsSave}
    onDeviceMoveSave={handleDeviceCoordsSave}
    onAfterMutation={async () => {
      await load();   // data reload — map + table dono refresh
    }}
    onJumpTo={handleJumpTo}
    onClose={() => setMapState(null)}
    breadcrumb={buildBreadcrumb(mapState.row, rowsById)}
    onBreadcrumbClick={handleBreadcrumbClick}
    isDarkMode={isDarkMode}
  />
)}

      {formState && (
        <DeviceFormDialog
          presetParent={formState.presetParent}
          editingRow={formState.editingRow}
          cityOptions={fullMeta.cityOptions.length ? fullMeta.cityOptions : cityOptions}
          streetOptionsFor={fullMeta.cityOptions.length ? fullMeta.streetOptionsFor : streetOptionsFor}
          deviceTypeOptions={fullMeta.deviceTypeOptions.length ? fullMeta.deviceTypeOptions : deviceTypeOptions}
          flatRows={flatRows}
          streetAnchors={fullMeta.streetAnchors}
          onCancel={() => setFormState(null)}
          onSubmit={handleDeviceFormSubmit}
        />
      )}

      {duplicateState && (
        <DuplicateDeviceDialog
          sourceRow={duplicateState.row}
          onCancel={() => setDuplicateState(null)}
          onSubmit={handleDuplicateSubmit}
        />
      )}

      {deleteConfirm && (
        <DeleteDeviceDialog
          row={deleteConfirm.row}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

/* ============================================================================
 * SECTION 6 — TABULAR VIEW
 * ==========================================================================*/

const TabularView = () => {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 50, totalRecords: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState('city_name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const hasMounted = useRef(false);

  const { cityOptions, streetOptionsFor } = useMemo(() => buildCityStreetOptions(records), [records]);
  const deviceTypeOptions = useMemo(() => collectDeviceTypeOptions(records), [records]);

  const [fullMeta, setFullMeta] = useState({
    cityOptions: [],
    streetOptionsFor: () => [],
    deviceTypeOptions: [],
    streetAnchors: new Map(),
  });
  useEffect(() => {
    let cancelled = false;
    fetchAllOnStreetMeta(navigate)
      .then((meta) => { if (!cancelled) setFullMeta(meta); })
      .catch((err) => console.error('Failed to load full on-street meta:', err));
    return () => { cancelled = true; };
  }, [navigate]);

  const [formState, setFormState] = useState(null);
  const [duplicateState, setDuplicateState] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchRecords = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);
      const res = await fetchOnStreetPage({ page, limit: perPage, sortBy, sortOrder, search: debouncedSearch, navigate });
      if (res?.success) {
        setRecords(res.data || []);
        setPagination({
          currentPage: res.page || page,
          perPage: res.limit || perPage,
          totalRecords: res.totalRecords || 0,
          totalPages: res.totalPages || 1,
        });
      }
    } catch (err) {
      console.error('Failed to load records:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasMounted.current) return;
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sortBy, sortOrder]);

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder((p) => (p === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(col); setSortOrder('ASC'); }
  };

  const refreshAll = async () => {
    await fetchRecords(pagination.currentPage, pagination.perPage);
  };

  const handleDeviceFormSubmit = async (payload) => {
    if (formState?.editingRow) {
      await updateOnStreetDeviceApi(formState.editingRow.pnd_number, payload);
    } else {
      await createOnStreetDeviceApi(payload);
    }
    setFormState(null);
    await refreshAll();
  };

  // ── Resolve row_id ──
  const resolveRowIdForStreet = useCallback((cityCd, streetCd, explicitRowId, loadedRows) => {
    if (explicitRowId) return explicitRowId;

    if (cityCd && streetCd && fullMeta.streetAnchors) {
      const key = `${cityCd}|${streetCd}`;
      const anchor = fullMeta.streetAnchors.get?.(key) || fullMeta.streetAnchors[key];
      if (anchor?.row_id) return anchor.row_id;
    }

    if (streetCd && Array.isArray(loadedRows)) {
      const match = loadedRows.find((r) => {
        if (String(r.street_cd) !== String(streetCd)) return false;
        if (cityCd && String(r.city_cd) !== String(cityCd)) return false;
        return !!extractRowId(r);
      });
      if (match) return extractRowId(match);
    }

    return '';
  }, [fullMeta.streetAnchors]);

  const handleDuplicateSubmit = async (payload) => {
    const rowId = resolveRowIdForStreet(payload.city_cd, payload.street_cd, payload.row_id, records);
    if (!rowId) {
      throw new Error('Is street par koi existing device ka row_id nahi mila. Backend team se kehna hoga ki /cp-map/pnd-street/all mein row_id field add karein.');
    }
    await duplicateOnStreetDeviceApi({
      row_id: rowId,
      pnd_number: payload.pnd_number,
      device_type: payload.device_type,
    });
    setDuplicateState(null);
    await refreshAll();
  };

  const handleDeleteConfirm = async () => {
    const raw = deleteConfirm.row.raw || deleteConfirm.row;
    const rowId = resolveRowIdForStreet(raw.city_cd, raw.street_cd, extractRowId(raw), records);
    if (!rowId) {
      throw new Error('Delete karne ke liye row_id chahiye. Backend team se kehna hoga ki /cp-map/pnd-street/all mein row_id field add karein.');
    }
    await deleteOnStreetDeviceApi(rowId);
    setDeleteConfirm(null);
    await refreshAll();
  };

  const rowActions = (row) => [
    // {
    //   label: 'Duplicate',
    //   icon: (
    //     <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#DBEAFE80] text-[#2563EB] dark:bg-[#DBEAFE15] dark:text-[#60A5FA]">
    //       <Copy className="h-4 w-4" />
    //     </span>
    //   ),
    //   onClick: () => setDuplicateState({ row: { level: 'DEVICE', raw: row } }),
    // },
    // {
    //   label: 'Delete',
    //   icon: (
    //     <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-red-500/10 text-red-500">
    //       <Trash2 className="h-4 w-4" />
    //     </span>
    //   ),
    //   onClick: () => setDeleteConfirm({ row: { raw: row } }),
    // },
  ];

  const columns = [
    { key: 'park_cd', label: 'Proj. Code', sortable: true },
    { key: 'city_name', label: 'Proj. Name', sortable: true },
    { key: 'street_cd', label: 'St. Code', sortable: true },
    { key: 'street_name', label: 'St. Name', sortable: true, render: (row) => row.street_name ? <SeeMore text={row.street_name} maxLength={30} /> : '—' },
    { key: 'pnd_number', label: 'PND No.', sortable: true, render: (row) => <span className="font-medium">{row.pnd_number}</span> },
    { key: 'latitude', label: 'Lat.', sortable: true },
    { key: 'longitude', label: 'Long.', sortable: true },
    {
      key: 'device_type', label: 'Type', sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getDeviceTypeColor(row.device_type)}`}>
          {row.device_type}
        </span>
      ),
    },
    {
      key: 'device_status', label: 'Status', sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          row.device_status === 'ACTIVE'
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
        }`}>
          {row.device_status}
        </span>
      ),
    },
    { key: 'device_zone', label: 'Zone', sortable: true, render: (row) => row.device_zone || '—' },
    { key: 'device_zone_name', label: 'Zone Name', sortable: true, render: (row) => row.device_zone_name || '—' },
    { key: 'device_status_date', label: 'Inactive Date', sortable: true, render: (row) => row.device_status_date || '—' },
    { key: 'device_active_date', label: 'Active Date', sortable: true, render: (row) => row.device_active_date || '—' },
  ];

  return (
    <>
      <DataTable
        title="On-Street Master Data"
        description="View parking sensor street data"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by street, city or PND..."
        headerActions={
          <Button size="sm" className="h-9 gap-1.5" onClick={() => setFormState({})}>
            <Plus className="h-4 w-4" />
            Add New Device
          </Button>
        }
        columns={columns}
        rows={records}
        rowKey="pnd_number"
        // rowActions={rowActions}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        pagination={pagination}
        onPageChange={(p) => fetchRecords(p, pagination.perPage)}
        onPerPageChange={(pp) => fetchRecords(1, Number(pp))}
        paginationTitle="records"
        loading={loading}
        emptyMessage="No street data found."
      />

      {formState && (
        <DeviceFormDialog
          presetParent={formState.presetParent}
          editingRow={formState.editingRow ? { raw: formState.editingRow } : null}
          cityOptions={fullMeta.cityOptions.length ? fullMeta.cityOptions : cityOptions}
          streetOptionsFor={fullMeta.cityOptions.length ? fullMeta.streetOptionsFor : streetOptionsFor}
          deviceTypeOptions={fullMeta.deviceTypeOptions.length ? fullMeta.deviceTypeOptions : deviceTypeOptions}
          flatRows={records}
          streetAnchors={fullMeta.streetAnchors}
          onCancel={() => setFormState(null)}
          onSubmit={handleDeviceFormSubmit}
        />
      )}

      {duplicateState && (
        <DuplicateDeviceDialog
          sourceRow={duplicateState.row}
          onCancel={() => setDuplicateState(null)}
          onSubmit={handleDuplicateSubmit}
        />
      )}

      {/* {deleteConfirm && (
        <DeleteDeviceDialog
          row={deleteConfirm.row}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleDeleteConfirm}
        />
      )} */}
    </>
  );
};

/* ============================================================================
 * SECTION 7 — TABBED WRAPPER
 * ==========================================================================*/

const OnStreetMasterData = () => {
  const [activeTab, setActiveTab] = useState('hierarchy');

  const tabBtn = (key) =>
    `px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
      activeTab === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
    }`;

  return (
    <div className="relative min-h-screen bg-background">
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 p-1 rounded-xl border border-border bg-card w-fit">
          <button className={tabBtn('hierarchy')} onClick={() => setActiveTab('hierarchy')}>Hierarchy View</button>
          <button className={tabBtn('tabular')} onClick={() => setActiveTab('tabular')}>Tabular View</button>
        </div>
      </div>

      <div className="">
        {activeTab === 'hierarchy' ? <HierarchyView /> : <TabularView />}
      </div>
    </div>
  );
};

export default OnStreetMasterData;